import { Body, Button, Header, Spacer } from '@spritz-finance/ui'
import produce from 'immer'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { Column } from '../../../../components/Layout/Column'
import { Row } from '../../../../components/Layout/Row'
import { PageLoading } from '../../../../components/PageLoading'
import {
    BillsDataQuery,
    BillType,
    FullBillFragment,
    useConnectBillsMutation,
} from '../../../../graphql/generated/graphql'
import { BILLS_QUERY } from '../../../../graphql/schema/liabilities'
import { sortOpenedDate } from '../../utils/filter'
import { BillIcon } from '../bills/BillIcon'
import { Bill } from '../bills/Bills'
import { BackButton } from './BackButton'
import { BillSelectRow } from './BillSelectRow'
import { useLinkBillContext } from './LinkBill'
import { LinkBill } from './types'

const BillsList = styled.div`
    width: 100%;
`

const SelectBills = ({ bills }: { bills: FullBillFragment[] }) => {
    const [connectBills, { loading }] = useConnectBillsMutation()
    const { setStep, onLinkBill } = useLinkBillContext()

    const linkBill = (bill: Bill) => {
        const billAccountId = bill.accountId
        if (bill.connected) {
            onLinkBill(billAccountId)
        } else {
            connectBills({
                variables: { accountIds: [billAccountId] },
                onCompleted: () => onLinkBill(billAccountId),
                update: (cache) => {
                    const query = cache.readQuery<BillsDataQuery>({
                        query: BILLS_QUERY,
                    })
                    if (!query?.bills) return

                    cache.writeQuery<BillsDataQuery>({
                        query: BILLS_QUERY,
                        data: {
                            bills: produce(query.bills, (draft) => {
                                draft.forEach((bill) => {
                                    if (billAccountId === bill.accountId) bill.connected = true
                                })
                                return draft
                            }),
                        },
                    })
                },
            })
        }
    }

    const sortedBills = useMemo(() => {
        return bills.sort(sortOpenedDate)
    }, [bills])

    return (
        <>
            {loading && <PageLoading />}

            <BillsList>
                {sortedBills.map((bill, index) => (
                    <React.Fragment key={bill.id}>
                        <BillSelectRow
                            bill={bill}
                            onItemClick={(bill) => {
                                if (!loading) linkBill(bill)
                            }}
                        />
                        {index !== bills.length - 1 && <Spacer height={24} />}
                    </React.Fragment>
                ))}
                <Spacer height={24} />
                <Row align="center">
                    <Button.CTA responsive onClick={() => setStep(LinkBill.AddNewBill)}>
                        Add another bill
                    </Button.CTA>
                </Row>
            </BillsList>
        </>
    )
}

export const AddExistingBill = () => {
    const { selectBillType, bills, setStep } = useLinkBillContext()
    const billsOfType = bills.filter(
        (bill) => bill.type === (selectBillType?.type as unknown as BillType)
    )
    if (!selectBillType) return null

    return (
        <Column align="center" style={{ position: 'relative' }}>
            <BackButton onClick={() => setStep(LinkBill.SelectBillType)} />
            {selectBillType.icon ? selectBillType.icon : <BillIcon type={selectBillType.type} />}
            <Spacer height={16} />
            <Body.Bold size={18}>{selectBillType.secondaryLine}</Body.Bold>

            <Spacer height={8} />
            <Body.Reg size={14} align="center">
                Here are some bills we were able to find for you.
            </Body.Reg>

            <Spacer height={24} />
            <SelectBills bills={billsOfType} />
        </Column>
    )
}
